import axios from "axios";
import { slipDebug, slipDebugError, slipDebugWarn } from "./paymentSlipDebug";

function getApiBaseUrl() {
  return (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
}

function readPaymentSlipDataFromStorage() {
  try {
    const slipRaw = localStorage.getItem("paymentSlipData");
    if (!slipRaw) return null;
    return JSON.parse(slipRaw);
  } catch {
    return null;
  }
}

function readPricingDraftFromStorage() {
  try {
    const raw = localStorage.getItem("sundorbonPricingDraft");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** True when booking is mango (Fruits / Mango product or mango pricing draft). */
export function isMangoPaymentSlip(data) {
  if (!data || typeof data !== "object") return false;

  if (data.pricingType === "mango" || data.isMangoBooking === true) {
    return true;
  }

  const draft = readPricingDraftFromStorage();
  if (draft?.pricingType === "mango") {
    return true;
  }

  const items = Array.isArray(data.producDetails) ? data.producDetails : [];
  return items.some((item) => {
    const typeId = String(item.productType ?? item.itemTypeId ?? "");
    const typeName = String(item.productTypeName ?? "").toLowerCase();
    const groupId = String(item.productGroup ?? item.itemCategoryId ?? "");
    return (
      typeId === "18" ||
      typeName === "mango" ||
      groupId === "19"
    );
  });
}

/**
 * Email for UploadPaymentSlip userEmail path param:
 * mango parcels → receiver email; all others → sender email.
 */
export function resolveUploadPaymentSlipEmail(explicitEmail, paymentSlipData) {
  slipDebug("resolveUploadPaymentSlipEmail", "input", {
    explicitEmail,
    hasPaymentSlipData: !!paymentSlipData,
  });

  if (explicitEmail && String(explicitEmail).trim()) {
    const email = String(explicitEmail).trim();
    slipDebug("resolveUploadPaymentSlipEmail", "OK — from explicit argument", {
      email,
    });
    return email;
  }

  const slip =
    paymentSlipData && typeof paymentSlipData === "object"
      ? paymentSlipData
      : readPaymentSlipDataFromStorage();

  if (!slip) {
    slipDebugWarn("resolveUploadPaymentSlipEmail", "STOP — no slip data");
    return "";
  }

  const isMango = isMangoPaymentSlip(slip);
  const email = isMango
    ? slip.receiverEmail
    : slip.senderEmail;

  if (email && String(email).trim()) {
    slipDebug("resolveUploadPaymentSlipEmail", "OK", {
      isMango,
      email: String(email).trim(),
      source: isMango ? "receiverEmail" : "senderEmail",
    });
    return String(email).trim();
  }

  slipDebugWarn("resolveUploadPaymentSlipEmail", "STOP — no email on slip", {
    isMango,
  });
  return "";
}

/** @deprecated Use resolveUploadPaymentSlipEmail — kept for callers that still import this. */
export function resolveReceiverEmail(explicitEmail, paymentSlipData) {
  return resolveUploadPaymentSlipEmail(explicitEmail, paymentSlipData);
}

export function resolveParcelId(data) {
  slipDebug("resolveParcelId", "input", {
    hasData: !!data,
    parcelId: data?.parcelId,
    ParcelId: data?.ParcelId,
    parcelID: data?.parcelID,
    id: data?.id,
  });

  if (!data || typeof data !== "object") {
    slipDebugWarn("resolveParcelId", "STOP — no data object");
    return null;
  }

  const raw =
    data.parcelId ?? data.ParcelId ?? data.parcelID ?? data.id;

  const n = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
  const result = Number.isFinite(n) && n > 0 ? n : null;

  if (result) {
    slipDebug("resolveParcelId", "OK", { parcelId: result });
  } else {
    slipDebugWarn("resolveParcelId", "STOP — could not resolve parcel id", {
      raw,
      parsed: n,
    });
  }

  return result;
}

export function getPaymentSlipUploadUrl(parcelId, userEmail) {
  const base = getApiBaseUrl();
  const id = String(parcelId);
  const email = encodeURIComponent(userEmail);
  const url = `${base}/Payment/UploadPaymentSlip/${id}/${email}`;
  slipDebug("getPaymentSlipUploadUrl", "built URL", { base, id, userEmail, url });
  return url;
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") || localStorage.getItem("token");
  slipDebug("getAuthHeaders", token ? "Bearer token present" : "no token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function uploadPaymentSlipPdf(
  parcelId,
  pdfBlob,
  fileName,
  { recordSerialNo, userEmail, paymentSlipData } = {},
) {
  slipDebug("uploadPaymentSlipPdf", "START", {
    parcelId,
    fileName,
    blobSize: pdfBlob?.size,
    blobType: pdfBlob?.type,
    recordSerialNo,
    userEmail,
    hasPaymentSlipData: !!paymentSlipData,
  });

  const id =
    typeof parcelId === "string" ? parseInt(parcelId, 10) : Number(parcelId);

  if (!Number.isFinite(id) || id <= 0) {
    slipDebugError("uploadPaymentSlipPdf", "STOP — invalid parcelId", { id });
    throw new Error("Missing parcelId for payment slip upload.");
  }

  const slipData =
    paymentSlipData && typeof paymentSlipData === "object"
      ? paymentSlipData
      : readPaymentSlipDataFromStorage();

  const email = resolveUploadPaymentSlipEmail(userEmail, slipData);
  if (!email) {
    const isMango = isMangoPaymentSlip(slipData);
    slipDebugError("uploadPaymentSlipPdf", "STOP — missing upload email", {
      isMango,
    });
    throw new Error(
      isMango
        ? "Missing receiver email for payment slip upload."
        : "Missing sender email for payment slip upload.",
    );
  }

  const formData = new FormData();
  const pdfFile =
    pdfBlob instanceof File
      ? pdfBlob
      : new File([pdfBlob], fileName, { type: "application/pdf" });
  formData.append("file", pdfFile);
  formData.append("parcelId", String(id));
  formData.append("userEmail", email);
  formData.append("recordSerialNo", recordSerialNo ? String(recordSerialNo) : "");

  const url = getPaymentSlipUploadUrl(id, email);

  slipDebug("uploadPaymentSlipPdf", "calling axios.post NOW", {
    url,
    isMango: isMangoPaymentSlip(slipData),
    formFields: ["file", "parcelId", "userEmail", recordSerialNo ? "recordSerialNo" : null].filter(Boolean),
  });

  try {
    const res = await axios.post(url, formData, {
      headers: {
        ...getAuthHeaders(),
        accept: "*/*",
      },
    });
    slipDebug("uploadPaymentSlipPdf", "SUCCESS", {
      status: res.status,
      data: res.data,
    });
    return res.data;
  } catch (err) {
    slipDebugError("uploadPaymentSlipPdf", "axios.post FAILED", {
      message: err?.message,
      status: err?.response?.status,
      responseData: err?.response?.data,
      url,
    });
    throw err;
  }
}
